import {AccessCode, Barcode, Box, LogLevel, NfcCard, Occupancy} from "@variocube/app-api";
import {ArrayUtils, getBoxTypes} from "@variocube/app-common";
import {soundMachine} from "@variocube/app-host/src/SoundMachine";
import {
	useActivateListener,
	useBarcodeListener,
	useBoxes,
	useCardListener,
	useKeypadListener,
	usePlatform,
	useStorageItemBlob,
	useStorageItemValue,
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {getSoundByKey} from "../accessibility";
import {useLocalization} from "../i18n";
import {CodeView} from "./CodeView";
import {DeliveryFlow} from "./DeliveryFlow";
import {DispatchFlow} from "./DispatchFlow";
import {DoorbellAccount, DoorBellFlow} from "./DoorBellFlow";
import {HomeView} from "./HomeView";
import {logger} from "./logger";
import {Account, AccountSettings, AssignedLocks, Carrier, UserAccessToken, UserAccessTokens} from "./model";
import {PickupFlow} from "./PickupFlow";
import {PoBoxEmptyView} from "./PoBoxEmptyView";
import {RegisterRfidView} from "./RegisterRfidView";

const log = logger.sub("PoBoxMain");

enum Flow {
	Delivery = "delivery",
	Dispatch = "dispatch",
	PickUp = "pickUp",
	DoorBell = "doorbell",
}

interface PickUp {
	assignedLocks: string[];
	occupancies: Occupancy[];
	boxes: Box[];
	preventReopen?: boolean;
}

export function PoBoxMain() {
	const platform = usePlatform();
	const [flow, setFlow] = useState<Flow>();
	const [pickUp, setPickUp] = useState<PickUp>();
	const [poBoxEmpty, setPoBoxEmpty] = useState<boolean>();
	const [registerUserAccessToken, setRegisterUserAccessToken] = useState<UserAccessToken>();
	const {t, language} = useLocalization();
	const carriers = useStorageItemValue<Carrier[]>("carriers");

	const boxes = useBoxes();
	const sizes = getBoxTypes(boxes)
		.filter(type => ["S", "M", "L", "XL", "XXL", "XS", "Cooled"].indexOf(type) >= 0);

	const assignedLocks = useStorageItemValue<AssignedLocks>("assignedLocks");

	const userAccessTokens = useStorageItemValue<UserAccessTokens>("userAccessTokens");

	const putTokenUrl = useStorageItemValue<string>("poBoxUrlAccessToken");

	const backgroundImageBlob = useStorageItemBlob("backgroundImage");
	const backgroundImageUrl = useMemo(() => backgroundImageBlob && URL.createObjectURL(backgroundImageBlob), [
		backgroundImageBlob,
	]);

	const accountSettings = useStorageItemValue<AccountSettings>("accountSettings");
	const accounts = useStorageItemValue<Account[]>("accounts");
	const doorbells = getDoorbells(accounts, accountSettings);

	const handleActivate = useCallback(() => {
		setFlow(undefined);
		setPickUp(undefined);
		setRegisterUserAccessToken(undefined);
		setPoBoxEmpty(false);
	}, []);
	useActivateListener(handleActivate);

	const verifyPickUpCode = useCallback(async (code: string, preventReopen?: boolean) => {
		verifyRegisterRfidCode(code);
		const codeAssignedLocks = (assignedLocks && assignedLocks[code]) || [];
		const occupancies = await platform.getOccupancies(code);

		if (ArrayUtils.hasItems(occupancies) || ArrayUtils.hasItems(codeAssignedLocks)) {
			const boxes = platform.boxes.filter(box => (
				(!platform.secondary && codeAssignedLocks.indexOf(box.lock) >= 0)
				|| (platform.secondary && box.secondaryLock && codeAssignedLocks.indexOf(box.secondaryLock) >= 0)
				|| (occupancies.find(occupancy => occupancy.boxNumber == box.number))
			));
			log.info("Starting pickup", {occupancies, assignedLocks: codeAssignedLocks, boxes});
			setPickUp({occupancies, assignedLocks: codeAssignedLocks, boxes, preventReopen});
		} else {
			if (preventReopen) {
				soundMachine.play(getSoundByKey("InvalidCode", "de"));
			}
			log.info("Invalid code", code);
			throw new Error("Invalid code.");
		}
	}, [platform, assignedLocks, userAccessTokens, language]);

	const handleBarcode = useCallback(async ({code}: Barcode) => {
		log.info("Received barcode", code);
		try {
			await platform.log(LogLevel.Trace, "Barcode scanned", {code});
			await verifyPickUpCode(atob(code));
		} catch (error) {
			log.error("Could not verify the scanned barcode.");
		}
	}, [platform, verifyPickUpCode]);
	useBarcodeListener(handleBarcode);

	const handleAccessCode = useCallback(async ({code}: AccessCode) => {
		log.info("Received access code", code);
		try {
			await platform.log(LogLevel.Trace, "Access code entered", {code});
			await verifyPickUpCode(code, true);
		} catch (error) {
			log.error("Could not verify the entered access code.");
		}
	}, [platform, verifyPickUpCode]);
	useKeypadListener(handleAccessCode);

	const handleCard = useCallback(async (card?: NfcCard) => {
		if (card) {
			if (userAccessTokens) {
				let token = userAccessTokens[card.uid];
				if (token) {
					const codeAssignedLocks = (assignedLocks && assignedLocks[token.token]) || [];
					let occupancies: Occupancy[] = await platform.getOccupancies();
					const boxes = platform.boxes;
					if (token.token && token.token === card.uid) {
						let myOccupancies = occupancies.filter(o => o.content.delivery.postBoxId === token.postBoxId);
						let occupiedBoxes = boxes.filter(box =>
							myOccupancies.find(occu => occu.boxNumber == box.number)
						);
						let assignedBoxes = boxes.filter(box =>
							codeAssignedLocks.find(l => l === box.lock || l === box.secondaryLock)
						);
						let boxesToOpen = occupiedBoxes.concat(assignedBoxes);
						if (boxesToOpen.length) {
							setPickUp({occupancies: myOccupancies, assignedLocks: [], boxes: boxesToOpen});
						} else {
							setPoBoxEmpty(true);
						}
					}
				}
			}
		}
	}, [platform, userAccessTokens]);
	useCardListener(handleCard);

	const verifyRegisterRfidCode = (code: string) => {
		log.info("Verifying RFID registration code", code);
		if (code && userAccessTokens) {
			const token = userAccessTokens[code];
			if (token?.code === code && !token?.token) {
				log.info("Registration code is valid");
				setRegisterUserAccessToken(token);
			}
		}
	};

	const handleDelivery = useCallback(() => setFlow(Flow.Delivery), []);
	const handleDispatch = useCallback(() => setFlow(Flow.Dispatch), []);
	const handlePickUp = useCallback(() => setFlow(Flow.PickUp), []);
	const handleDoorBell = useCallback(() => setFlow(Flow.DoorBell), []);

	if (pickUp) {
		return (
			<PickupFlow
				occupancies={pickUp.occupancies}
				assignedLocks={pickUp.assignedLocks}
				boxes={pickUp.boxes}
				preventReopen={pickUp.preventReopen}
				onExit={handleActivate}
			/>
		);
	}

	if (registerUserAccessToken) {
		return (
			<RegisterRfidView
				userAccessTokens={userAccessTokens}
				userAccessToken={registerUserAccessToken}
				putTokenUrl={putTokenUrl || ""}
				onBack={handleActivate}
			/>
		);
	}

	if (poBoxEmpty) {
		return <PoBoxEmptyView onBack={handleActivate} />;
	}

	switch (flow) {
		case Flow.PickUp:
			return (
				<CodeView
					onBack={handleActivate}
					title={t("PickupCodeTitle")}
					description={t("PickupCodeDescription")}
					onVerify={verifyPickUpCode}
				/>
			);

		case Flow.Delivery:
			return (
				<DeliveryFlow
					sizes={sizes}
					carriers={carriers || []}
					onExit={handleActivate}
				/>
			);

		case Flow.Dispatch:
			return (
				<DispatchFlow
					sizes={sizes}
					onExit={handleActivate}
				/>
			);
		case Flow.DoorBell:
			return (
				<DoorBellFlow
					doorbells={doorbells}
					onExit={handleActivate}
				/>
			);
	}
	return (
		<HomeView
			onDelivery={handleDelivery}
			onDispatch={handleDispatch}
			onPickUp={handlePickUp}
			onDoorBell={handleDoorBell}
			backgroundImageUrl={backgroundImageUrl}
			doorbell={doorbells.length > 0}
		/>
	);
}

function getDoorbells(accounts: Account[] | undefined, accountSettings: AccountSettings | undefined) {
	console.log("accounts", accounts);
	console.log("accountSettings", accountSettings);
	if (!accounts || !accountSettings) {
		return [];
	}
	return accounts.map(account => ({
		...account,
		doorBellSerialNr: accountSettings[account.postBoxId]?.doorBellSerialNr,
	}))
		.filter(hasDoorbell) ?? [];
}

function hasDoorbell(account: Account & { doorBellSerialNr: string | undefined }): account is DoorbellAccount {
	return Boolean(account.doorBellSerialNr);
}
